import { useEffect, useMemo, useState } from "react";
import { Download, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { offersClient } from "../../lib/offersApi";
import MarginTrendChart from "../../components/MarginTrendChart";
import { EmptyLog } from "../../components/illustrations/EmptyIllustrations";
import { EmptyState } from "../../components/ui/empty-state";
import { GridSkeletonRows } from "../../components/ui/grid-skeleton";

import { BACKEND } from "../../lib/backend";

function Kpi({ label, value, tone = "default", sub }) {
  const colors = {
    default: { bg: "var(--bg-surface)", fg: "var(--text-primary)" },
    green: { bg: "#DCFCE7", fg: "#16A34A" },
    amber: { bg: "#FEF3C7", fg: "#B45309" },
    red: { bg: "#FEE2E2", fg: "#DC2626" },
    blue: { bg: "#DBEAFE", fg: "#1D4ED8" },
  }[tone];
  return (
    <div
      className="rounded-lg p-3 flex-1 min-w-[140px]"
      style={{ background: colors.bg, border: "1px solid var(--border-subtle)" }}
    >
      <div
        className="text-[10px] uppercase tracking-wide font-medium"
        style={{ color: "var(--text-muted)" }}
      >
        {label}
      </div>
      <div
        className="tq-cell-mono text-[20px] font-semibold mt-0.5"
        style={{ color: colors.fg }}
      >
        {value}
      </div>
      {sub && (
        <div className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

export default function LogOfferte() {
  const [sinceDays, setSinceDays] = useState(90);
  const [data, setData] = useState({ kpis: {}, rows: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    ruolo: "",
    famiglia: "",
    status: "",
  });

  useEffect(() => {
    setLoading(true);
    offersClient
      .get(`/reports/log-offerte?since_days=${sinceDays}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error("Errore caricamento log"))
      .finally(() => setLoading(false));
  }, [sinceDays]);

  const filteredRows = useMemo(() => {
    return (data.rows || []).filter((r) => {
      if (filter.ruolo && r.ruolo !== filter.ruolo) return false;
      if (filter.famiglia && r.famiglia !== filter.famiglia) return false;
      if (filter.status && r.status !== filter.status) return false;
      return true;
    });
  }, [data, filter]);

  const k = data.kpis || {};

  const exportCsv = () => {
    window.open(
      `${BACKEND}/api/reports/log-offerte/export.csv?since_days=${sinceDays}`,
      "_blank"
    );
  };

  return (
    <div className="max-w-[1600px] mx-auto p-6 space-y-4" data-testid="log-offerte-page">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-[22px] font-semibold flex items-center gap-2"
            style={{ color: "var(--text-primary)" }}
          >
            <TrendingUp className="h-5 w-5" />
            Log offerte
          </h1>
          <p
            className="text-[13px] mt-0.5"
            style={{ color: "var(--text-secondary)" }}
          >
            Registro storico offerte · ultimi {sinceDays} giorni · {filteredRows.length} voci
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(parseInt(e.target.value, 10))}
            className="h-9 px-2 rounded-md text-[13px]"
            style={{ border: "1px solid var(--border-default)" }}
            data-testid="period-select"
          >
            <option value={7}>Ultimi 7 giorni</option>
            <option value={30}>Ultimi 30 giorni</option>
            <option value={90}>Ultimi 90 giorni</option>
            <option value={180}>Ultimi 6 mesi</option>
            <option value={365}>Ultimo anno</option>
          </select>
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[13px] font-medium"
            style={{
              background: "var(--accent-primary)",
              color: "var(--text-on-accent)",
            }}
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4" />
            Esporta CSV
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="flex gap-2 flex-wrap">
        <Kpi label="Totali" value={k.total ?? "—"} />
        <Kpi label="Vinte" value={k.vinte ?? 0} tone="green" />
        <Kpi label="Perse" value={k.perse ?? 0} tone="red" />
        <Kpi label="Win rate" value={`${k.win_rate_pct ?? 0}%`} tone="blue" />
        <Kpi
          label="Valore vinto"
          value={`€ ${(k.valore_vinto ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          tone="green"
        />
        <Kpi
          label="Ricarico medio"
          value={`${k.ricarico_medio_pct ?? 0}%`}
        />
        <Kpi
          label="Approvazione rich."
          value={k.approvazione_richiesta ?? 0}
          tone="amber"
        />
        <Kpi label="Sotto soglia" value={k.sotto_soglia ?? 0} tone="red" />
      </div>

      {/* Margin trend */}
      <div
        className="rounded-lg p-4"
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}
      >
        <div
          className="text-[11px] uppercase tracking-wide font-medium mb-2"
          style={{ color: "var(--text-muted)" }}
        >
          Margine nel tempo (ricarico medio applicato, ultimi {Math.min(90, sinceDays)} giorni)
        </div>
        <MarginTrendChart days={Math.min(90, sinceDays)} height={180} />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <select
          value={filter.ruolo}
          onChange={(e) => setFilter({ ...filter, ruolo: e.target.value })}
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
          data-testid="filter-ruolo"
        >
          <option value="">Tutti i ruoli</option>
          <option value="Importatore">Importatore</option>
          <option value="Rivenditore">Rivenditore</option>
        </select>
        <select
          value={filter.famiglia}
          onChange={(e) => setFilter({ ...filter, famiglia: e.target.value })}
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
          data-testid="filter-famiglia"
        >
          <option value="">Tutte le famiglie</option>
          <option value="Standard">Standard</option>
          <option value="Tubi inox / acciaio carbonio">Tubi</option>
          <option value="Navale">Navale</option>
        </select>
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
          data-testid="filter-status"
        >
          <option value="">Tutti gli stati</option>
          <option value="inviata">Inviata</option>
          <option value="vinta">Vinta</option>
          <option value="persa">Persa</option>
          <option value="in_lavorazione">In lavorazione</option>
        </select>
      </div>

      {/* Table */}
      <div
        className="rounded-lg overflow-hidden"
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}
      >
        <table className="w-full text-[12px]" data-testid="log-offerte-table">
          <thead>
            <tr style={{ background: "var(--bg-subtle)", color: "var(--text-secondary)" }}>
              {[
                "Data",
                "N° Offerta",
                "Cliente",
                "Zona",
                "Ruolo",
                "Famiglia",
                "Valore €",
                "Ricarico %",
                "Margine PV %",
                "Approvata",
                "Status",
                "Giudizio",
              ].map((h) => (
                <th
                  key={h}
                  className="text-left px-2 py-2 text-[11px] font-semibold uppercase tracking-wide"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <GridSkeletonRows cols={12} rows={6} />}
            {!loading && filteredRows.length === 0 && (
              <tr>
                <td colSpan={12}>
                  <EmptyState
                    testid="log-empty"
                    illustration={<EmptyLog />}
                    title="Nessuna offerta nel periodo selezionato"
                    description="Prova ad ampliare l'intervallo temporale o a rimuovere i filtri attivi."
                  />
                </td>
              </tr>
            )}
            {!loading &&
              filteredRows.map((r, i) => (
                <tr
                  key={`${r.offer_number}-${i}`}
                  style={{ borderTop: "1px solid var(--border-subtle)" }}
                  data-testid={`log-row-${r.offer_number}`}
                >
                  <td className="px-2 py-1.5 text-[11px]" style={{ color: "var(--text-muted)" }}>
                    {(r.data || "").slice(0, 10)}
                  </td>
                  <td className="px-2 py-1.5 tq-cell-mono text-[11px]">
                    {r.offer_number}
                  </td>
                  <td className="px-2 py-1.5">{r.cliente || "—"}</td>
                  <td className="px-2 py-1.5 text-[11px]" style={{ color: "var(--text-muted)" }}>
                    {r.zona || "—"}
                  </td>
                  <td className="px-2 py-1.5">{r.ruolo || "—"}</td>
                  <td className="px-2 py-1.5">{r.famiglia || "—"}</td>
                  <td className="px-2 py-1.5 text-right tq-cell-mono">
                    {(r.valore ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-2 py-1.5 text-right tq-cell-mono">
                    {r.ricarico_pct != null ? `${r.ricarico_pct.toFixed(1)}%` : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right tq-cell-mono">
                    {r.margine_pv_pct != null ? `${r.margine_pv_pct.toFixed(1)}%` : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-[11px]">{r.approvata ? "SI" : "—"}</td>
                  <td className="px-2 py-1.5 text-[11px]">{r.status}</td>
                  <td className="px-2 py-1.5 text-[10px]">{r.judgment || "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
